import React from 'react';
import { Box, Typography, Button, Popover, Paper } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Visibility as VisibilityIcon } from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from 'recharts';
import { SEARCE_COLORS } from '../../theme';

export interface EmployeesByTierBarRow {
  name: string;
  tier1: number;
  tier2: number;
  tier3: number;
  tier4: number;
}

interface EmployeesByTierBarChartProps {
  data: EmployeesByTierBarRow[];
  viewMode: 'product' | 'bu';
  onViewModeChange: (mode: 'product' | 'bu') => void;
  legendAnchorEl: HTMLElement | null;
  onLegendOpen: (el: HTMLElement) => void;
  onLegendClose: () => void;
}

/**
 * Bar chart for employees by tier (product-wise or BU-wise).
 * Includes Legend popover and Product/BU toggle.
 */
const EmployeesByTierBarChart: React.FC<EmployeesByTierBarChartProps> = ({
  data,
  viewMode,
  onViewModeChange,
  legendAnchorEl,
  onLegendOpen,
  onLegendClose,
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        border: `1px solid ${SEARCE_COLORS.neutral_200}`,
        borderRadius: 2,
        backgroundColor: SEARCE_COLORS.white,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: SEARCE_COLORS.textPrimary }}>
          Employees by Tier
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={(e) => onLegendOpen(e.currentTarget)}
            startIcon={<VisibilityIcon />}
            sx={{ textTransform: 'none', mr: 1 }}
          >
            Legend
          </Button>
          <Popover
            open={Boolean(legendAnchorEl)}
            anchorEl={legendAnchorEl}
            onClose={onLegendClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Tier Legend
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, backgroundColor: SEARCE_COLORS.success, borderRadius: '50%' }} />
                  <Typography variant="body2">Tier 1</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, backgroundColor: SEARCE_COLORS.primary, borderRadius: '50%' }} />
                  <Typography variant="body2">Tier 2</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, backgroundColor: '#FFC107', borderRadius: '50%' }} />
                  <Typography variant="body2">Tier 3</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, backgroundColor: SEARCE_COLORS.error, borderRadius: '50%' }} />
                  <Typography variant="body2">Tier 4</Typography>
                </Box>
              </Box>
            </Box>
          </Popover>
          <Button
            variant={viewMode === 'product' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => onViewModeChange('product')}
            sx={{ textTransform: 'none' }}
          >
            Product Wise
          </Button>
          <Button
            variant={viewMode === 'bu' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => onViewModeChange('bu')}
            sx={{ textTransform: 'none' }}
          >
            BU Wise
          </Button>
        </Box>
      </Box>
      <Box sx={{ height: 350, width: '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={alpha(SEARCE_COLORS.neutral_300, 0.5)} />
            <XAxis
              dataKey="name"
              angle={-45}
              textAnchor="end"
              height={80}
              tick={{ fill: SEARCE_COLORS.textSecondary, fontSize: 11 }}
              interval={0}
              tickFormatter={(value) => {
                let label = value;
                if (viewMode === 'bu') {
                  label = label.replace('Delivery | ', '');
                }
                return label.length > 6 ? `${label.substring(0, 6)}...` : label;
              }}
            />
            <YAxis
              tick={{ fill: SEARCE_COLORS.textSecondary, fontSize: 12 }}
              label={{
                value: 'Number of Employees',
                angle: -90,
                position: 'insideLeft',
                dy: 50,
                style: { fill: SEARCE_COLORS.textSecondary, fontSize: 12 },
              }}
            />
            <RechartsTooltip
              cursor={{ fill: alpha(SEARCE_COLORS.primary, 0.1) }}
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  return (
                    <Box
                      sx={{
                        backgroundColor: SEARCE_COLORS.white,
                        border: `1px solid ${SEARCE_COLORS.neutral_200}`,
                        borderRadius: 1,
                        p: 1.5,
                        boxShadow: `0 2px 8px ${alpha(SEARCE_COLORS.primary, 0.15)}`,
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        {label}
                      </Typography>
                      {payload.map((entry, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: entry.color,
                            }}
                          />
                          <Typography variant="body2" sx={{ color: SEARCE_COLORS.textPrimary }}>
                            {entry.name}: {entry.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  );
                }
                return null;
              }}
            />
            <Bar dataKey="tier1" name="Tier 1" fill={SEARCE_COLORS.success} />
            <Bar dataKey="tier2" name="Tier 2" fill={SEARCE_COLORS.primary} />
            <Bar dataKey="tier3" name="Tier 3" fill="#FFC107" />
            <Bar dataKey="tier4" name="Tier 4" fill={SEARCE_COLORS.error} />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
};

export default EmployeesByTierBarChart;
